import React, { useEffect, useState, useRef } from "react";
import { getPreferences, getAccountValues } from '../store/user/thunks';
import SubmitFeedbackDialog from './feedback/SubmitFeedbackDialog';
import { getTradesPage, getTradesStats } from '../store/trades/thunks';
import { getPnlByYear, getLeaderboardPage, getTickerPerformance, getTagPerformance, getEmotionalPerformance } from '../store/analytics/thunks';
import { getNotifications, getUnreadCount, markNotificationsRead, markAllNotificationsRead } from '../store/social/thunks';
import { getJournalEntries } from '../store/journal/thunks';
import '../styles/navbar.css';
import { returnInTZ } from '../utils/timezone';
import TrialToastContent from '../components/TrialToastContent'; // adjust path if needed
import AIChat from './AIChat';
import {
  Flex,
  Heading,
  Avatar,
  Text,
  Box,
  Center,
  useColorMode,
  Image,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  useDisclosure,
  useToast,
  Button,
  Spacer,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Badge,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
  Portal,
  Tooltip} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { RiStockFill } from "react-icons/ri";
import { BsSun, BsMoon, BsList, BsBell, BsBellFill } from "react-icons/bs";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { SiSwagger } from "react-icons/si";
import { VscFeedback } from "react-icons/vsc";
import { useSelector, useDispatch } from "react-redux";
import logo from '../logo/mttlogo512.png';
import { TOKEN } from '../api/axiosInstance';

import useToastNotification from "../hooks/useToastNotification";
import { PAGE_NAME } from '../data/pages';
const API_URL = process.env.REACT_APP_API_URL;
const SWAGGER_URL = API_URL+'apidocs/';
const APP_VERSION = "2.2.0";

export default function Navbar({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToastNotification();
  const toast = useToast();
  const dispatch = useDispatch();
  const { trade } = useSelector((state) => state.trade);
  const today = new Date();
  const year = today.getFullYear();
  const [reportBugFlag, setReportBugFlag] = useState(false);
  const [infoPopup, setInfoPopup] = useState(false);
  const { profilepic } = useSelector((state) => state.user);
  const hasProfilePicture = ((profilepic && Object.keys(profilepic).length > 0) ? (true):(false));
  const isLoggedIn = (((user && Object.keys(user).length > 2) || window.localStorage.getItem(TOKEN)) ? (true):(false));
  const unreadCount = useSelector((state) => state.social.unreadCount) || 0;
  const notifications = useSelector((state) => state.social.notifications) || [];
  const [notifOpen, setNotifOpen] = useState(false);


  const { onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const { colorMode, toggleColorMode } = useColorMode();
  const previousColorMode = useRef(colorMode);

  const [hideNavbar, setHideNavbar] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const previousIsCollapsed = useRef(isCollapsed);

  useEffect(() => {
    const pageText = PAGE_NAME.find(el => el.page === location.pathname)?.text;
  
    if (pageText === "Payment" || pageText === "Upgrade Account") {
      setHideNavbar(true);
      if (toast.isActive("trial_toast")) {
        toast.close("trial_toast"); 
      }
    } else {
      setHideNavbar(false);

      let toastMessage = {}
      if (
        isLoggedIn === true &&
        user?.user_id !== undefined &&
        user?.has_paid === 0 &&
        user?.created_at
      ) {
        const rawTimestamp = user.created_at.replace(' ', 'T') + 'Z'; // → "2025-06-11T00:51:36Z"
        const trialStart = new Date(rawTimestamp); // now parsed as UTC
        const expiryDate = new Date(trialStart.getTime() + 5 * 24 * 60 * 60 * 1000);

        const formattedExpiry = expiryDate.toLocaleString(navigator.language || 'en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        toastMessage = { message: "You're on a 5 Day Free Trial", expiry: formattedExpiry }
      } else if (isLoggedIn === false) {
        toast.close("trial_toast");
      }
  
      const renderToast = () => (
        <TrialToastContent
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          colorMode={colorMode}
          navigate={navigate}
          toastMessage={toastMessage}
        />
      );
  
      if (toast.isActive("trial_toast") && (previousColorMode.current !== colorMode || previousIsCollapsed.current !== isCollapsed)) {
        toast.update("trial_toast", {
          render: renderToast,
          isClosable: false
        });
      }
  
      if (!toast.isActive("trial_toast") && isLoggedIn && user?.has_paid === 0) {
        toast({
          id: "trial_toast",
          variant: 'solid',
          status: 'info',
          duration: null,
          render: renderToast,
          isClosable: false
        });
      }
  
      previousColorMode.current = colorMode;
      previousIsCollapsed.current  = isCollapsed;
    }
  }, [
    location.pathname,
    PAGE_NAME,
    colorMode,
    isCollapsed,
    navigate,
    isLoggedIn,
    user
  ]);


  // Poll unread notification count
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getUnreadCount({}));
      const interval = setInterval(() => {
        dispatch(getUnreadCount({}));
      }, 300000); // every 5 minutes
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, dispatch]);

  const handleOpenNotifications = async () => {
    setNotifOpen(true);
    await dispatch(getNotifications({}));
  };

  const handleMarkAllRead = async () => {
    await dispatch(markAllNotificationsRead({}));
    await dispatch(getUnreadCount({}));
  };

  useEffect(() => {
    displayPageName();
  }, [PAGE_NAME]);
  const toggleInfoButton = (e) => {
    setInfoPopup(true);
  }

  const handleCancelInfoPopup = (e) => {
    setInfoPopup(false);
  };

  const handleGoToSwagger = (e) => {
    window.location.href = SWAGGER_URL; // Replace with your actual Swagger URL
    //window.open(SWAGGER_URL, '_blank');
  };

  const handleHome = async (e) => {
    await dispatch(getTradesStats());
    await dispatch(getPreferences());
    let filters = {};
    filters.date = returnInTZ(today.toISOString());
    await dispatch(getAccountValues({ filters }));
    filters = {};
    filters.page = 1;
    filters.numrows = 5;
    filters.trade_date = 'NULL';
    await dispatch(getTradesPage({ filters }));
    handleDeleteLocal();
    navigate("/");
  }

  const handlePnlCalendar = async (e) => {
    handleDeleteLocal();
    await dispatch(getPnlByYear({ year }));
    navigate("/PnlCalendar");
  }

  const handleGroups = async (e) => {
    handleDeleteLocal(); 
    navigate("/groups")
  }

  const handleJournal = async (e) => {
    const today = returnInTZ(new Date().toISOString());
    let info = window.localStorage.getItem('journalInfo') ? JSON.parse(window.localStorage.getItem('journalInfo')) : {};
    let hasInfo = Object.keys(info).length > 0;
    let date = today;
    if (hasInfo && info.selectedDate) {
      date = info.selectedDate;
    }
    await dispatch(getJournalEntries({ date })); 
    // Save the date used for the API call
    const journalInfo = {
      selectedDate: date,
      source: 'navbar',
      lastApiCall: new Date().toISOString()
    };
    window.localStorage.setItem('journalInfo', JSON.stringify(journalInfo));
    // Notify Journal via custom event too (works even if component isn't remounted)
    window.dispatchEvent(new CustomEvent('journalNavigate', { detail: { selectedDate: date, source: 'navbar' } }));
    // Navigate to journal; Journal.js will read from localStorage
    navigate("/journal");
  }

  const handleLeaderboard = async (e) => {
    const encodedTimeFilter = encodeURIComponent("All Time");
    const encodedValueFilter = encodeURIComponent("Total PNL");
    const filters = {};
    filters.page = 1;
    filters.numrows = 25;
    filters.time_filter = encodedTimeFilter;
    filters.value_filter = encodedValueFilter;
    await dispatch(getLeaderboardPage({ filters }));
    handleDeleteLocal();
    navigate("/leaderboard");
  }

  const handleProfile = async (e) => {
    await dispatch(getPreferences()); 
    //await dispatch(getProfilePicture());
    handleDeleteLocal();
    navigate("/profile");
  }

  const handleTrades = async (e) => {
    handleDeleteLocal();
    let filters = {};
    filters.page = 1;
    filters.numrows = 100;
    await dispatch(getTradesPage({ filters }));
    navigate("/summary");
  }

  const handleTickerAnalytics = async (e) => {
    handleDeleteLocal();
    await dispatch(getTickerPerformance());
    navigate('/ticker-analytics');
  }

  const handleTagAnalytics = async (e) => {
    handleDeleteLocal();
    await dispatch(getTagPerformance());
    navigate('/tag-analytics');
  }

  const handleEmotionalAnalytics = async (e) => {
    handleDeleteLocal();
    await dispatch(getEmotionalPerformance());
    navigate('/emotional-analytics');
  }

  const handleInsights = async (e) => {
    handleDeleteLocal();
    navigate('/insights');
  }

  const handleOptionsAnalytics = async (e) => {
    handleDeleteLocal();
    navigate('/options-analytics');
  }

  const handleStudy = async (e) => {
    handleDeleteLocal();
    navigate('/study');
  }

  const handleReportBug = (e) => {
    setReportBugFlag(true);
    onOpen();
  }

  const handleDeleteLocal = () => {
    window.localStorage.removeItem('userInfo');
    window.localStorage.removeItem('journalInfo');
    window.localStorage.removeItem('tradeInfo');
    window.localStorage.removeItem('feedbackInfo');
    window.localStorage.removeItem('updateTradeInfo');
    window.localStorage.removeItem('updateBulkTradeInfo');
    window.localStorage.removeItem('updateUserInfo');
    window.localStorage.removeItem('HomeFilters');
    window.localStorage.removeItem('CalendarFilters');
    window.localStorage.removeItem('SummaryFilters');
    window.localStorage.removeItem('LeaderboardFilters');
  }
  
  const displayPageName = () =>{
    const pageText = PAGE_NAME.find(el => el.page === location.pathname)?.text
    if (pageText && pageText !== "None"){
      return (
        <Text className='mtt-pagename' color={colorMode === 'light' ? 'white' : 'gray.700'} fontSize="sm" fontWeight="semibold">
          ›&nbsp;{pageText}
        </Text>
      );
    }
    return null;
  }

  const handleGotoLogin = (e) => {
    navigate("/login");
  };

  const handleGotoSignup = (e) => {
    navigate("/signup");
  };
  
  return (
    <Flex justify="space-between" bg={colorMode === 'light' ? "blue.500" : "blue.200"} boxShadow="0 2px 8px 0 rgba(0,0,0,0.25), 0 1px 3px -1px rgba(0,0,0,0.2)" position="sticky" top={0} zIndex={1100} >
      {((user && Object.keys(user).length > 2) && !(trade && Object.keys(trade).length > 2)) ? (
        <HStack >
        <Image
          class='logo'
          src={logo}
          alt='Error'
          onClick={(e) => handleHome(e.target.value)}
        />
        <Heading className='mtt-brand' color={colorMode === 'light' ? 'white' : 'gray.800'} fontSize="lg" onClick={(e) => handleHome(e.target.value)}>
          My&#8203;Trading&#8203;Tracker
          <Icon as={RiStockFill} ml={1}></Icon>
        </Heading>
        {displayPageName()}
        </HStack>
      ) : (
        <HStack >
        <Image
          class='logo-alone'
          src={logo}
          alt='Error'
        />
        <Heading className='mtt-brand-alone' color={colorMode === 'light' ? 'white' : 'gray.800'} fontSize="lg">
          My&#8203;Trading&#8203;Tracker
          <Icon as={RiStockFill} ml={1}></Icon>
        </Heading>
        </HStack>
      )}
      {((user && Object.keys(user).length > 2) && !(trade && Object.keys(trade).length > 2)) ? (
        <>
        {hideNavbar ? (
          <>
          </>
        ) : (
        <>
        <Spacer /><Center >
          <span>
          <HStack gap='0' padding={3}>
            <Menu>
              <MenuButton
                as={Button}
                aria-label='Navigation'
                leftIcon={<Icon as={BsList} boxSize={4} />}
                size="sm"
                variant="ghost"
                color={colorMode === 'light' ? 'white' : 'gray.800'}
                _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                _active={{ bg: colorMode === 'light' ? 'whiteAlpha.300' : 'blackAlpha.300' }}
              >
                Menu
              </MenuButton>
              <MenuList zIndex={1000} fontSize="sm">
                <MenuItem onClick={(e) => handleHome(e)}>Home</MenuItem>
                <MenuItem onClick={(e) => handleTrades(e)}>Trades</MenuItem>
                <MenuItem onClick={(e) => handleLeaderboard(e)}>Leaderboard</MenuItem>
                <MenuItem onClick={(e) => handlePnlCalendar(e)}>Calendar</MenuItem>
                <MenuItem onClick={(e) => handleJournal(e)}>Journal</MenuItem>
                <MenuItem onClick={(e) => handleGroups(e)}>Groups</MenuItem>
                <MenuItem onClick={(e) => handleInsights(e)}>Insights</MenuItem>
                <MenuItem onClick={(e) => handleTickerAnalytics(e)}>Ticker Analytics</MenuItem>
                <MenuItem onClick={(e) => handleTagAnalytics(e)}>Tag Analytics</MenuItem>
                <MenuItem onClick={(e) => handleEmotionalAnalytics(e)}>Emotional Analytics</MenuItem>
                <MenuItem onClick={(e) => handleOptionsAnalytics(e)}>Options Analytics</MenuItem>
                <MenuItem onClick={(e) => handleStudy(e)}>Study</MenuItem>
                <MenuItem onClick={(e) => handleReportBug(e)}>Feedback</MenuItem>
                <MenuItem onClick={() => navigate('/myfeedback')}>My Feedback</MenuItem>
              </MenuList>
            </Menu>
            <AIChat inline={false} sidebar={true} />
            <Popover
              isOpen={notifOpen}
              onClose={() => setNotifOpen(false)}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Box position="relative" display="inline-block">
                  <Tooltip label="Notifications" fontSize="xs" hasArrow>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      color={colorMode === 'light' ? 'white' : 'gray.800'}
                      _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                      aria-label="Notifications"
                      icon={<Icon as={unreadCount > 0 ? BsBellFill : BsBell} boxSize={4} />}
                      onClick={handleOpenNotifications}
                    />
                  </Tooltip>
                  {unreadCount > 0 && (
                    <Badge
                      bg="red.500"
                      color="white"
                      borderRadius="full"
                      position="absolute"
                      top="-1"
                      right="-1"
                      fontSize="0.6em"
                      minW="16px"
                      textAlign="center"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Box>
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="350px" maxH="400px" zIndex={1500}>
                  <PopoverCloseButton />
                  <PopoverHeader>
                    <HStack justifyContent="space-between" pr={6}>
                      <Text fontWeight="bold" fontSize="sm">Notifications</Text>
                      {unreadCount > 0 && (
                        <Button size="xs" variant="ghost" colorScheme="blue" onClick={handleMarkAllRead}>
                          Mark all read
                        </Button>
                      )}
                    </HStack>
                  </PopoverHeader>
                  <PopoverBody overflowY="auto" maxH="340px" p={0}>
                    {notifications.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" p={4} textAlign="center">No notifications</Text>
                    ) : (
                      <VStack spacing={0} align="stretch">
                        {notifications.map((n) => (
                          <Box
                            key={n.notification_id}
                            p={3}
                            borderBottomWidth="1px"
                            bg={n.is_read ? 'transparent' : (colorMode === 'light' ? 'blue.50' : 'blue.900')}
                            cursor="pointer"
                            _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}
                            onClick={async () => {
                              if (!n.is_read) {
                                await dispatch(markNotificationsRead({ notification_ids: [n.notification_id] }));
                                await dispatch(getUnreadCount({}));
                                await dispatch(getNotifications({}));
                              }
                            }}
                          >
                            <Text fontSize="xs" fontWeight={n.is_read ? "normal" : "bold"}>{n.title}</Text>
                            {n.message && <Text fontSize="xs" color={colorMode === 'light' ? 'gray.600' : 'gray.300'} noOfLines={2}>{n.message}</Text>}
                            <Text fontSize="2xs" color="gray.400" mt={1}>{new Date(n.created_at).toLocaleDateString()}</Text>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
            <Tooltip label="Theme" fontSize="xs" hasArrow>
              <IconButton
                size="sm"
                variant="ghost"
                color={colorMode === 'light' ? 'white' : 'gray.800'}
                _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                aria-label="Toggle color mode"
                icon={<Icon as={colorMode === 'light' ? BsSun : BsMoon} boxSize={4} />}
                onClick={toggleColorMode}
              />
            </Tooltip>
            <Tooltip label="Info" fontSize="xs" hasArrow>
              <IconButton
                size="sm"
                variant="ghost"
                color={colorMode === 'light' ? 'white' : 'gray.800'}
                _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                aria-label="Information"
                icon={<Icon as={IoIosInformationCircleOutline} boxSize={4} />}
                onClick={toggleInfoButton}
              />
            </Tooltip>
            <Tooltip label="Feedback" fontSize="xs" hasArrow>
              <IconButton
                size="sm"
                variant="ghost"
                color={colorMode === 'light' ? 'white' : 'gray.800'}
                _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                aria-label="Feedback"
                icon={<Icon as={VscFeedback} boxSize={4} />}
                onClick={(e) => handleReportBug(e)}
              />
            </Tooltip>
            <SubmitFeedbackDialog
              isOpen={reportBugFlag}
              onClose={() => setReportBugFlag(false)}
            />
            {infoPopup}
              <AlertDialog
              motionPreset='slideInBottom'
              isOpen={infoPopup}
              leastDestructiveRef={cancelRef}
              onClose={e => handleCancelInfoPopup(e)}
              isCentered={true}
              closeOnOverlayClick={true}
            >
              <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  <Flex alignItems="center" width="100%">
                    <IoIosInformationCircleOutline/>
                    <Text paddingLeft={1}>Information</Text>
                  </Flex>
                </AlertDialogHeader>
                <AlertDialogCloseButton />
                <AlertDialogBody>
                  <Center>
                  <HStack>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="API Documentation"
                    icon={<Icon as={SiSwagger} />}
                    onClick={e => handleGoToSwagger(e)}
                  />
                  <Text>
                    API Documentation
                  </Text>
                  </HStack>
                  </Center>
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Text as='i'>
                    v{APP_VERSION}
                  </Text>
                </AlertDialogFooter>
              </AlertDialogContent>
              </AlertDialogOverlay>
            </AlertDialog>
        </HStack>
        </span>
        <HStack paddingRight={2} ml={2}>
        {user.first_name !== "" ? (
        <Text className="mtt-username" color={colorMode === 'light' ? 'white' : 'gray.800'} fontSize="sm" fontWeight="medium">
          {user.first_name}
        </Text>
        ) : (
          null
        )}
        <Avatar  
          size="sm" 
          src={hasProfilePicture ? profilepic.profile_picture_url : null}
          m={2} 
          onClick={(e) => handleProfile(e.target.value)}
        />
        </HStack>
        </Center>
        </>
        )}
        </>
      ) : (
        <HStack gap='0' padding={3}>
          {location.pathname === "/" ? (
            <>
            <HStack spacing={1} mr={2} display={{ base: 'none', md: 'flex' }}>
              {[
                { label: 'Features', id: 'features-grid' },
                { label: 'Pricing', id: 'pricing' },
                { label: 'FAQ', id: 'faq' },
                { label: 'About', id: 'about' },
              ].map((item) => (
                <Button
                  key={item.id}
                  size="sm"
                  variant="ghost"
                  color={colorMode === 'light' ? 'white' : 'gray.800'}
                  _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                  onClick={() => {
                    const el = document.getElementById(item.id);
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </HStack>
            <Button size="sm" variant="ghost" color={colorMode === 'light' ? 'white' : 'gray.800'} _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }} onClick={(e) => handleGotoLogin(e.target.value)}>
              Log In
            </Button>
            <Button size="sm" variant="ghost" color={colorMode === 'light' ? 'white' : 'gray.800'} _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }} onClick={(e) => handleGotoSignup(e.target.value)}>
              Register
            </Button>
            <IconButton
              size="sm"
              variant="ghost"
              color={colorMode === 'light' ? 'white' : 'gray.800'}
              _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
              aria-label="Toggle color mode"
              icon={<Icon as={colorMode === 'light' ? BsSun : BsMoon} />}
              onClick={toggleColorMode}
            />
            </>
          ) : (
            <IconButton
              size="sm"
              variant="ghost"
              color={colorMode === 'light' ? 'white' : 'gray.800'}
              _hover={{ bg: colorMode === 'light' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
              aria-label="Toggle color mode"
              icon={<Icon as={colorMode === 'light' ? BsSun : BsMoon} />}
              onClick={toggleColorMode}
            />
          )}
        </HStack>
      )
      }
    </Flex>
  );
}
